import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Typography } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

const { Title, Paragraph } = Typography

interface PopupNoticeProps {
  isActive: boolean
  title: {
    en: string
    zh: string
  }
  description: {
    en: string
    zh: string
  }
  imageUrl: string
  frequency?: 'always' | 'once-per-session' | 'once-per-day' | 'once-per-week'
}

const PopupNotice: React.FC<PopupNoticeProps> = ({ 
  isActive, 
  title, 
  description, 
  imageUrl, 
  frequency = 'once-per-session' 
}) => {
  const [visible, setVisible] = useState(false)
  const [modalWidth, setModalWidth] = useState<string | number>('min(1200px, 95vw)')
  const imageRef = useRef<HTMLImageElement>(null)
  const { i18n } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  // Handle image load to calculate optimal modal width
  const handleImageLoad = () => {
    if (!imageRef.current) return
    
    const img = imageRef.current
    const naturalWidth = img.naturalWidth
    const naturalHeight = img.naturalHeight
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    
    // Calculate maximum available width (leaving some margin)
    const maxAvailableWidth = Math.min(viewportWidth * 0.95, 1200)
    const maxAvailableHeight = Math.min(viewportHeight * 0.8, 700)
    
    // Calculate optimal width based on image dimensions
    let optimalWidth = naturalWidth
    let optimalHeight = naturalHeight
    
    // Scale down if image is too large
    if (optimalWidth > maxAvailableWidth) {
      const ratio = maxAvailableWidth / optimalWidth
      optimalWidth = maxAvailableWidth
      optimalHeight = optimalHeight * ratio
    }
    
    if (optimalHeight > maxAvailableHeight) {
      const ratio = maxAvailableHeight / optimalHeight
      optimalHeight = maxAvailableHeight
      optimalWidth = optimalWidth * ratio
    }
    
    // Ensure minimum width for readability
    const minWidth = Math.min(400, viewportWidth * 0.8)
    optimalWidth = Math.max(optimalWidth, minWidth)
    
    // Add padding for modal content (title, description, buttons)
    const modalPadding = 48 // 24px * 2 for left and right padding
    const finalWidth = Math.min(optimalWidth + modalPadding, maxAvailableWidth)
    
    setModalWidth(finalWidth)
  }

  // Check if popup should be shown based on frequency setting
  const shouldShowPopup = () => {
    if (!isActive || (!title?.en && !title?.zh && !description?.en && !description?.zh && !imageUrl)) {
      return false
    }

    if (frequency === 'always') {
      return true
    }

    const storageKey = `popupNoticeShown_${frequency}`
    const lastShown = localStorage.getItem(storageKey)

    if (!lastShown) {
      return true
    }

    const lastShownDate = new Date(lastShown)
    const now = new Date()

    switch (frequency) {
      case 'once-per-session':
        // Check sessionStorage for this session
        return !sessionStorage.getItem('popupNoticeShown')
      
      case 'once-per-day':
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        return lastShownDate < oneDayAgo
      
      case 'once-per-week':
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        return lastShownDate < oneWeekAgo
      
      default:
        return false
    }
  }

  useEffect(() => {
    if (shouldShowPopup()) {
      setVisible(true)
    }
  }, [isActive, title, description, imageUrl, frequency])

  const handleClose = () => {
    setVisible(false)
    
    // Mark popup as shown based on frequency
    const now = new Date().toISOString()
    
    if (frequency === 'once-per-session') {
      sessionStorage.setItem('popupNoticeShown', 'true')
    } else if (frequency !== 'always') {
      const storageKey = `popupNoticeShown_${frequency}`
      localStorage.setItem(storageKey, now)
    }
  }

  // Don't render if not active or no content
  if (!isActive || (!title?.en && !title?.zh && !description?.en && !description?.zh && !imageUrl)) {
    return null
  }

  const popupTitle = getLocalizedText(title)
  const popupDescription = getLocalizedText(description)

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" type="primary" onClick={handleClose}>
          Close
        </Button>
      ]}
      closeIcon={<CloseOutlined />}
      centered
      width={modalWidth}
      styles={{
        body: { padding: '24px' }
      }}
    >
      <div style={{ textAlign: 'center' }}>
        {imageUrl && (
          <div style={{ marginBottom: 16 }}>
            <img
              ref={imageRef}
              src={imageUrl}
              alt={popupTitle || 'Notice'}
              style={{ 
                maxWidth: '100%', 
                maxHeight: 700, 
                objectFit: 'contain',
                borderRadius: 8,
                display: 'block',
                margin: '0 auto'
              }}
              onLoad={handleImageLoad}
              onError={(e) => {
                e.currentTarget.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwsDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8A+B4Hpg+p4Ah8CXwBfAl8AXwJfAF8C0BfAF8AXwBfAl8AXwBfAl8AXwBfAF8CmgBbAlrAU0BbAlrAU0BbAlrAU0BbAlrAU0CbAd7l/QfgG2h9EyPDMf7lOqLF9u/2/FpvqQBgEAAAAASUVORK0Jggg=";
              }}
            />
          </div>
        )}
        
        {popupTitle && (
          <Title level={3} style={{ marginBottom: 16, color: '#1890ff' }}>
            {popupTitle}
          </Title>
        )}
        
        {popupDescription && (
          <Paragraph style={{ 
            fontSize: 16, 
            color: '#666', 
            lineHeight: 1.6,
            marginBottom: 0 
          }}>
            {popupDescription}
          </Paragraph>
        )}
      </div>
    </Modal>
  )
}

export default PopupNotice